import { LitElement, html, css} from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { PastTrailData, VoyagePastTrailModel } from '../../../src/models/voyage-past-trail-model';
import { GetFormattedDate } from '../../../src/utils/helpers';
import { compassRose } from '../../../src/utils/weather-utils';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import '../../voyage-spinner/src/voyage-spinner';
import { getFormattedDataSource } from '../../../src/utils/utils';

export enum VoyagePastTrailPopupEvents {
    Close = 'close'
}

@customElement('voyage-past-trail-popup')
export class VoyagePastTrailPopup extends LitElement {
    @property({type: Object}) data: PastTrailData | null = null;
    @property({type: Boolean}) private _weatherExpanded: boolean = false;
    @property({ attribute: false }) private _weatherDataIsFetching: boolean = false;

    private _weatherFetched: boolean = false;

    static get styles() {
        return [ css`
            .popup {
                display: flex;
                flex-direction: column;
                background: var(--voyage-past-trail-popup-background);
                width: 242px;
                
                font-family: 'MaerskText';
                text-align: left;
                border-radius: 5px;
                user-select: none;
            }

            .top-container {
                padding: 14px 14px 0 14px;
            }

            .weather-container {
                padding: 14px;
            }

            .text { 
                font-size: 12px;
                font-weight: normal;
                line-height: 16px;
                color: var(--neutrals-grey-10);
            }

            .bold-text {
                font-size: 12px;
                line-height: 16px;
                font-weight: bold;
                color: var(--neutrals-grey-10);
            }

            .custom-button {
                font-size: 12px;
                text-align: end;
                line-height: 16px;
                font-weight: bold;
                color: var(--neutrals-grey-10);
                padding: 0;
                border: none;
                background: none;
                cursor: pointer;
            }

            .custom-button > svg{
                width: 11px;
                height: 11px;
                rotate: 90;
                fill: var(--neutrals-grey-10, white);
            }

            .two-columns {
                display: grid;
                grid-template-columns: auto auto;
                place-items: left right;
            }

            voyage-spinner{
                width: 25px;
                height: 25px;
            }
        `];
    }
    
    render() {
        const dataSource = this.data?.positionData.source;
        const formattedDataSource = dataSource ? getFormattedDataSource(dataSource!) : "--";
        return html`
            <div class="popup">
                <div class="top-container">
                    <div class="two-columns">
                        <div class="text date-and-source">${GetFormattedDate(this.data?.timestamp!)} UTC ${formattedDataSource}</div>
                        <button class="custom-button close" @click=${this._close}>X</button>
                    </div>
                    
                    <div class="text speed-and-heading">${this.data?.speed} kt ${this.data?.heading}° (${compassRose(this.data?.heading!)})</div>
                    <div class="text">Draft: ${this.data?.draft || '--'}</div>
                </div>
                <div class="weather-container">
                    <div class="two-columns">
                        <div class="bold-text weather-forecast">Weather forecast ${GetFormattedDate(this.data?.weatherForecast?.timestamp!)} UTC</div>
                        ${this._weatherDataIsFetching ?
                            html `<voyage-spinner .transparent=${true}></voyage-spinner>` :
                            html `<button id="weatherbutton" class="custom-button" @click=${this._onWeatherClick}>
                                    ${this._weatherExpanded ? html`${SvgIcons.chevronUp}` : html`${SvgIcons.chevronDown}`}
                                 </button>`
                        }
                    </div>
                    ${this._weatherExpanded ? html`
                        <div class="text weather">Wind 50 m: ${this.data?.wind50M || "--"}</div>
                        <div class="text weather">Wind gusts 50 m: ${this.data?.windGusts50M || "--"} </div>
                        <div class="text weather">Wave: ${this.data?.wave || '--'}</div>
                        <div class="text weather">Swell: ${this.data?.swell || '--'}</div>
                        <div class="text weather">Risk wave height: ${this.data?.riskWaveHeight || '--'}</div>
                        <div class="text weather">Currents: ${this.data?.currents || '--'}</div>
                        ` : html``}
                </div>
            </div>
        `;
    }

    private _close() {
        this.dispatchEvent(new Event(VoyagePastTrailPopupEvents.Close));
    }

    private async _onWeatherClick() {
        if (!this.data) {
            return;
        }

        if (this._weatherFetched) {
            this._weatherExpanded = !this._weatherExpanded;
            return;
        }

        this._weatherDataIsFetching = true;
        const dataWithWeather = await VoyagePastTrailModel!.fetchWeatherSinglePosition(this.data);
        if (dataWithWeather) {
            this.data = dataWithWeather;
            this._weatherExpanded = !this._weatherExpanded;
            this._weatherFetched = true;
        }

        this._weatherDataIsFetching = false;
    }
};
